import { useList, useDelete, useNavigation, useOne, useCreate, useUpdate } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ArrowLeft, Save, Loader2 } from 'lucide-react';
import type { Activity } from '@natsepa/database';
import { ImageManager, type ManagedImage } from '../../components/ImageManager';
import { BilingualField } from '../../components/BilingualField';
import { BilingualArrayField } from '../../components/BilingualArrayField';

// Helper function to generate slug from title
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function ActivitiesList() {
  const { data, isLoading, error } = useList<Activity>({ resource: 'activities' });
  const { mutate: deleteOne } = useDelete();
  const { edit, create } = useNavigation();

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this activity?')) {
      deleteOne({ resource: 'activities', id });
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading activities: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Activities</h1>
        <button
          onClick={() => create('activities')}
          className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Activity
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Duration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.data.map((activity) => (
              <tr key={activity.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{activity.name}</div>
                  <div className="text-sm text-gray-500">{activity.slug}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  {activity.duration || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  {activity.price ? `IDR ${activity.price.toLocaleString()}` : 'Free'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => edit('activities', activity.id)}
                      className="p-2 text-gray-400 hover:text-gold-600 transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(activity.id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {data?.data.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No activities found. Click "Add Activity" to create one.
          </div>
        )}
      </div>
    </div>
  );
}

// Create Activity
export function ActivitiesCreate() {
  const navigate = useNavigate();
  const { mutate: createActivity, isPending } = useCreate();
  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    duration: '',
    price: 0,
    capacity: 0,
    schedule: '',
    includes: [] as { en: string; id: string }[],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.nameId) {
      alert('Please fill in the name');
      return;
    }

    const slug = form.slug || generateSlug(form.nameId);

    createActivity(
      {
        resource: 'activities',
        values: { ...form, slug, name: form.nameId || form.nameEn, description: form.descriptionId || form.descriptionEn },
      },
      {
        onSuccess: (data) => {
          const newId = data?.data?.id;
          if (newId) {
            navigate(`/activities/edit/${newId}`);
          } else {
            navigate('/activities');
          }
        },
      }
    );
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/activities')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Activity</h1>
          <p className="text-sm text-gray-500 mt-1">Add a new guest activity</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        {/* Basic Info */}
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>

          <BilingualField
            label="Name"
            valueEn={form.nameEn}
            valueId={form.nameId}
            onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder={form.nameId ? generateSlug(form.nameId) : 'auto-generated'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Activity Details */}
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Activity Details</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <input
                type="text"
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
                placeholder="e.g., 2 hours"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (IDR)</label>
              <input
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Capacity</label>
              <input
                type="number"
                value={form.capacity}
                onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
              <input
                type="text"
                value={form.schedule}
                onChange={(e) => setForm({ ...form, schedule: e.target.value })}
                placeholder="e.g., Daily 9AM-5PM"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Note about images */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> You can add images after creating the activity.
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/activities')}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50"
          >
            {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Create & Continue
          </button>
        </div>
      </form>
    </div>
  );
}

// Edit Activity
export function ActivitiesEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useOne<Activity>({ resource: 'activities', id: id! });
  const { mutate: updateActivity, isPending } = useUpdate();

  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    duration: '',
    price: 0,
    capacity: 0,
    schedule: '',
    includes: [] as { en: string; id: string }[],
    sortOrder: 0,
  });
  const [images, setImages] = useState<ManagedImage[]>([]);

  useEffect(() => {
    if (data?.data) {
      const activity = data.data;
      // Load bilingual includes from the paired includesEn/includesId arrays
      const enInc = (activity as any).includesEn || [];
      const idInc = (activity as any).includesId || activity.includes || [];
      const includes: { en: string; id: string }[] = Array.from(
        { length: Math.max(enInc.length, idInc.length) },
        (_, i) => ({ en: enInc[i] || idInc[i] || '', id: idInc[i] || enInc[i] || '' })
      );
      setForm({
        slug: activity.slug || '',
        nameEn: (activity as any).nameEn || (activity as any).name_en || activity.name || '',
        nameId: (activity as any).nameId || (activity as any).name_id || activity.name || '',
        descriptionEn: (activity as any).descriptionEn || (activity as any).description_en || activity.description || '',
        descriptionId: (activity as any).descriptionId || (activity as any).description_id || activity.description || '',
        shortDescriptionEn: (activity as any).shortDescriptionEn || (activity as any).short_description_en || activity.shortDescription || '',
        shortDescriptionId: (activity as any).shortDescriptionId || (activity as any).short_description_id || activity.shortDescription || '',
        duration: activity.duration || '',
        price: activity.price || 0,
        capacity: activity.capacity || 0,
        schedule: activity.schedule || '',
        sortOrder: (activity as any).sortOrder ?? 0,
        includes,
      });
      if (activity.images) {
        setImages(
          activity.images.map((url, index) => ({
            url,
            imageType: 'gallery' as const,
            sortOrder: index,
          }))
        );
      }
    }
  }, [data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    updateActivity(
      {
        resource: 'activities',
        id: id!,
        values: {
          ...form,
          images: images.map((img) => img.url),
        },
      },
      {
        onSuccess: () => {
          navigate('/activities');
        },
      }
    );
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/activities')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Activity</h1>
          <p className="text-sm text-gray-500 mt-1">{form.nameId || form.nameEn || 'Activity details'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Form (2 columns) */}
        <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>

            <BilingualField
              label="Name"
              valueEn={form.nameEn}
              valueId={form.nameId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })}
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-gray-50"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Description</h2>

            <BilingualField
              label="Short Description"
              valueEn={form.shortDescriptionEn}
              valueId={form.shortDescriptionId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'shortDescriptionEn' : 'shortDescriptionId']: value })}
              multiline
              rows={2}
            />

            <BilingualField
              label="Full Description"
              valueEn={form.descriptionEn}
              valueId={form.descriptionId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'descriptionEn' : 'descriptionId']: value })}
              multiline
              rows={4}
            />
          </div>

          {/* Activity Details */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Activity Details</h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                <input
                  type="text"
                  value={form.duration}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })}
                  placeholder="e.g., 2 hours"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price (IDR)</label>
                <input
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Capacity</label>
                <input
                  type="number"
                  value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
                <input
                  type="text"
                  value={form.schedule}
                  onChange={(e) => setForm({ ...form, schedule: e.target.value })}
                  placeholder="e.g., Daily 9AM-5PM"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Includes */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">What's Included</h2>
            <BilingualArrayField
              label="Includes"
              items={form.includes}
              onChange={(includes) => setForm({ ...form, includes })}
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/activities')}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50"
            >
              {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Save Changes
            </button>
          </div>
        </form>

        {/* Right: Images (1 column) */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity Images</h2>
            <ImageManager
              entityType="activity"
              entityId={id!}
              images={images}
              imageType="gallery"
              multiple={true}
              maxImages={10}
              onChange={setImages}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
